import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import Search from 'classes/StandingData/TimeUnit/Search/Search';
import TimeUnit from 'classes/StandingData/TimeUnit/Search/TimeUnit';
import TimeUnitList from 'classes/StandingData/TimeUnit/Search/TimeUnitList';
import TimeUnitDetailComponent from 'components/desktop/StandingData/TimeUnit/Detail/TimeUnitDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useAirNavigation from 'hooks/useAirNavigation';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const TimeUnitManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [timeUnitList, setTimeUnitList] = useState<TimeUnitList>(new TimeUnitList());
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { params } = useAirNavigation();
	const displayOverlay = params.get('id') ? true : false;

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getTimeUnits();
		} else {
			await getTimeUnits();
		}
		setSearch(clone(search));
	};

	const getTimeUnits = async () => {
		setIsLoading(true);
		try {
			const response = await timeUnitList.callApi(search.mapToRequest());
			if (response) {
				timeUnitList.mapFromApi(response);
				setTimeUnitList(clone(timeUnitList));
			}
		} finally {
			setIsLoading(false);
		}
	};

	const getSuggestedTimeUnits = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await timeUnitList.search(searchTerm, props.archive ?? false);
		} else {
			timeUnitList.filtered = [];
		}

		setTimeUnitList(clone(timeUnitList));
		return timeUnitList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getTimeUnits();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		handleChangeId(null);
	};

	const handleArchiveTimeUnit = async (timeUnit: TimeUnit): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			cancelButtonText: t('_general:CANCEL'),
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			icon: 'warning',
			iconColor: '#ff0004',
			showCancelButton: true,
			text: t('standingData:TIMEUNIT_ARCHIVE_DIALOG'),
			title: t('standingData:ARCHIVE_TITLE'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await timeUnit.callApiDelete();
				await Swal.fire({
					confirmButtonColor: '#00cc07',
					title: t('standingData:TIMEUNIT_ARCHIVE_CONFIRMATION'),
				});
				await getTimeUnits();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleRestoreTimeUnit = async (timeUnit: TimeUnit): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			cancelButtonText: t('_general:CANCEL'),
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			icon: 'warning',
			iconColor: '#00cc07',
			showCancelButton: true,
			text: t('standingData:TIMEUNIT_RESTORE_DIALOG'),
			title: t('standingData:RESTORE_TITLE'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await timeUnit.callApiRestore();
				await Swal.fire({
					confirmButtonColor: '#00cc07',
					title: t('standingData:TIMEUNIT_RESTORE_CONFIRMATION'),
				});
				await getTimeUnits();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
	};

	const getButtonDataArchive = (input: TimeUnit): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveTimeUnit(input),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataRestore = (input: TimeUnit): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreTimeUnit(input),
			};
		}
	};

	const renderButtons = (input: TimeUnit): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = (): Row[] => {
		return timeUnitList.all.map((timeUnit: TimeUnit) => {
			return {
				id: timeUnit.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<div className="flex-fill">{timeUnit.name}</div>
						<div>{renderButtons(timeUnit)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = () => {
		return (
			<>
				<main className="container">
					<div className="d-flex flex-row justify-content-between">
						{!props.archive ? (
							<>
								<h1>{t('standingData:TIMEUNIT_MANAGEMENT')}</h1>
								<div className="d-flex flex-gap-25">
									<BtnNew
										onClick={handleChangeIdToNew}
										name={t('standingData:CREATE_TIMEUNIT')}
									/>
								</div>
							</>
						) : (
							<h1>{t('standingData:TIMEUNIT_ARCHIVE')}</h1>
						)}
					</div>
					<br />
					<div className="row">
						<div className="col-lg-12">
							<div className="input-group mb-3">
								<AdvancedSearchInput
									debounceMilliSeconds={350}
									getSuggestedItems={getSuggestedTimeUnits}
									handleSearch={handleSearch}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									triggerSearchLetterAmount={1}
								/>
							</div>
						</div>
					</div>
					<br />
					<LoadingAnimation isLoading={isLoading} />
					<p>{timeUnitList.totalCount + ' ' + t('_general:TIMEUNITS')}</p>
					<StandingDataList
						archive={props.archive}
						displayOverlay={displayOverlay}
						rows={renderListRows()}
						overlay={<TimeUnitDetailComponent />}
						handleOverlayOpen={handleOverlayOpen}
						handleOverlayClose={handleOverlayClose}
					/>
					<br />
					<Pagination
						countOfAllListItems={timeUnitList.totalCount}
						pageIndex={search.getPageIndex()}
						setPageIndex={handleChangePageIndex}
						listItemAmount={search.getPageSize()}
						maxNumberOfButtons={maxNumberOfButtons}
					/>
				</main>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default TimeUnitManagementComponent;
